import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { PublicAnalyticsConfig } from '../lib/types';
import { useCookieConsent } from '../lib/cookieConsent';
import { loadGA, trackPageview, unloadGA } from '../lib/ga';
import { loadCloudflareWA } from '../lib/cloudflareAnalytics';
import { loadClarity, unloadClarity } from '../lib/clarity';
import { loadPostHog, posthogPageview, unloadPostHog } from '../lib/posthogClient';
import { CookieConsentBanner } from './CookieConsentBanner';

/**
 * Drives all public-site analytics + the cookie-consent banner. Mounted once at the app
 * root; it self-gates — the logged-in portals (/app, /admin, /affiliate) are not public,
 * so nothing runs there.
 *
 * Consent-gated (load only after analytics consent, tear down on withdrawal):
 *   - Google Analytics (GA4), Microsoft Clarity, PostHog.
 * No consent needed (cookieless):
 *   - Cloudflare Web Analytics. (Search-engine ownership is via DNS-TXT, not code.)
 *
 * Each tool's id is returned by /platform/analytics only when its feature is enabled, so a
 * disabled integration never loads even with consent.
 */
const APP_PREFIXES = ['/app', '/admin', '/affiliate'];

function isPublicPath(pathname: string): boolean {
  return !APP_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function PublicAnalytics() {
  const { pathname } = useLocation();
  const onPublic = isPublicPath(pathname);
  const analyticsAllowed = useCookieConsent((s) => s.consent?.analytics === true);

  const { data } = useQuery({
    queryKey: ['public-analytics-config'],
    queryFn: () => api<PublicAnalyticsConfig>('/platform/analytics', { auth: false }),
    enabled: onPublic,
    staleTime: 60 * 60 * 1000,
  });

  const consentActive = onPublic && analyticsAllowed;
  const gaId = data?.measurementId ?? null;
  const clarityId = data?.clarityProjectId ?? null;
  const phKey = data?.posthogApiKey ?? null;
  const phHost = data?.posthogHost ?? 'https://eu.i.posthog.com';
  const phProxy = data?.posthogProxyHost ?? null;

  // ── Consent-gated trackers: load when allowed + configured, else tear down ──
  useEffect(() => {
    if (consentActive && gaId) loadGA(gaId);
    else unloadGA();
  }, [consentActive, gaId]);

  useEffect(() => {
    if (consentActive && clarityId) loadClarity(clarityId);
    else unloadClarity();
  }, [consentActive, clarityId]);

  useEffect(() => {
    if (consentActive && phKey) void loadPostHog(phKey, phHost, phProxy);
    else unloadPostHog();
  }, [consentActive, phKey, phHost, phProxy]);

  // SPA pageviews on every public route change while the tracker is active.
  useEffect(() => {
    if (consentActive && gaId) trackPageview(gaId, pathname);
    if (consentActive && phKey) posthogPageview();
  }, [consentActive, gaId, phKey, pathname]);

  // ── Cookieless / no-consent ────────────────────────────────────────────────
  const cloudflareToken = data?.cloudflareToken ?? null;
  useEffect(() => {
    if (onPublic && cloudflareToken) loadCloudflareWA(cloudflareToken);
  }, [onPublic, cloudflareToken]);

  if (!onPublic) return null;
  // Banner shows when there's a consent-requiring tracker configured. Name only the
  // tools actually enabled so the copy never advertises a disabled provider.
  const enabledProviderNames = [
    gaId ? 'Google Analytics' : null,
    clarityId ? 'Microsoft Clarity' : null,
    phKey ? 'PostHog' : null,
  ].filter((n): n is string => n !== null);
  const consentTrackerConfigured = enabledProviderNames.length > 0;
  return (
    <CookieConsentBanner
      trackersConfigured={consentTrackerConfigured}
      providers={enabledProviderNames}
    />
  );
}
